<template>
  <pp-container
    :fluid="true"
    tag="form"
    @submit.prevent="onSubmit"
  >
    <pp-form-message
      v-if="invalidMagicLink && magicToken"
      type="error"
    >
      Appointment not found, please verify the link or check in with the form below.
    </pp-form-message>
    <h2
      v-else
      class="text-center"
    >
      Please Check In
    </h2>
    <pp-row
      class="mt-2"
    >
      <pp-col
        cols="12"
        sm="6"
      >
        <pp-input-text
          v-model="visit.firstName"
          label="First Name"
          :disabled="formSubmitting"
          :error-messages="errorMessagesHandler('firstName', 'Field')"
          @blur="trackFormProgress('firstName')"
        />
      </pp-col>
      <pp-col
        cols="12"
        sm="6"
      >
        <pp-input-text
          v-model="visit.lastName"
          label="Last Name"
          :disabled="formSubmitting"
          :error-messages="errorMessagesHandler('lastName', 'Field')"
          @blur="trackFormProgress('lastName')"
        />
      </pp-col>
      <pp-col
        cols="12"
        sm="6"
      >
        <pp-input-text
          v-model="visit.email"
          label="Email"
          :disabled="formSubmitting"
          :error-messages="errorMessagesHandler('email', 'E-mail')"
          type="email"
          @blur="trackFormProgress('email')"
        />
      </pp-col>
      <pp-col
        cols="12"
        sm="6"
      >
        <pp-input-text
          v-model="visit.phone"
          v-mask="'(###) ### ####'"
          label="Mobile"
          :disabled="formSubmitting"
          :error-messages="errorMessagesHandler('phone', 'Phone')"
          type="tel"
          @blur="trackFormProgress('phone')"
        />
      </pp-col>
    </pp-row>
    <pp-row>
      <pp-col>
        <pp-input-checkbox
          v-model="visit.consent"
          :disabled="formSubmitting"
          label="I agree to receive text messages regarding my health and account. Texts are not encrypted and could potentially be viewed by third parties. Carrier charges may apply."
        />
      </pp-col>
    </pp-row>
    <pp-row>
      <pp-col>
        <vue-recaptcha
          :load-recaptcha-script="true"
          :sitekey="recaptchaSiteKey"
          @verify="onVerificationOfRecaptcha"
        />
        <div class="v-messages red--text pl-3 mb-4 recaptcha-error-message">
          <template v-if="showRecaptchaErrorMessage">
            Please verify you are human
          </template>
        </div>
      </pp-col>
    </pp-row>
    <pp-button
      :loading="formSubmitting"
      :disabled="formSubmitting"
      type="primary"
      :submit="true"
      text="Check In"
    />
    <pp-snack-bar
      v-model="showErrorMessage"
      :snack-message="errorMessage"
    />
  </pp-container>
</template>

<script>
import Vuex from 'vuex'
import VueRecaptcha from 'vue-recaptcha'
import {
  RECAPTCHA_SITE_KEY,
  RECAPTCHA_SITE_KEY_CYPRESS,
  LOCAL_STORAGE_CHANNEL_ID_KEY,
  APPOINTMENT_TYPE_DROP_IN,
  APPOINTMENT_TYPE_SCHEDULED
} from '@/constants'
import { VueMaskDirective } from 'v-mask'
import { required, email } from 'vuelidate/lib/validators'
import { validPhone } from 'pp-fe-utils'
import { validationMixin } from 'vuelidate'

const { mapGetters } = Vuex

export default {
  name: 'ViewsCheckIn',
  components: {
    VueRecaptcha
  },
  directives: { mask: VueMaskDirective },
  mixins: [validationMixin],
  data () {
    return {
      formSubmitting: false,
      passedRecaptcha: process.env.VUE_APP_DISABLE_CAPTCHA === 'true',
      showRecaptchaErrorMessage: false,
      visit: {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        consent: false
      },
      showErrorMessage: false,
      errorMessage: '',
      checkingMagicLink: true,
      invalidMagicLink: false,
      magicToken: this.$route.query.token
    }
  },
  computed: {
    ...mapGetters({
      practiceInfo: 'practice/practiceInfo',
      appointment: 'scheduler/appointment'
    }),
    recaptchaSiteKey () {
      return window.Cypress ? RECAPTCHA_SITE_KEY_CYPRESS : RECAPTCHA_SITE_KEY
    }
  },
  watch: {
    practiceInfo () {
      this.onPageLoad()
    }
  },
  created () {
    if (this.practiceInfo) {
      this.onPageLoad()
    }
  },
  methods: {
    onPageLoad () {
      if (this.magicToken) {
        this.startSession()
      } else {
        this.checkingMagicLink = false
      }
    },
    trackFormProgress (field) {
      this.$v.visit[field].$touch()
      this.$ppAnalytics.trackEvent({
        action: 'form_input',
        label: field
      })
    },
    onSubmit () {
      if (this.formSubmitting) {
        return
      }

      this.checkRecaptcha()

      if (this.$v.$invalid) {
        this.$v.$touch()
        return
      }

      if (!this.$v.$invalid && this.passedRecaptcha) {
        // disable submit form
        this.formSubmitting = true
        this.createAppointmentAndPatient()
      }
    },
    createAppointmentAndPatient () {
      this.$store.dispatch('scheduler/createAppointmentAndPatient', {
        ...this.visit,
        phone: this.visit.phone.replace(/[^0-9]/g, ''),
        practiceUuid: this.practiceInfo.practice_id
      }).then(() => {
        this.$ppAnalytics.trackEvent({ action: 'check_in' })
        this.startSession()
      }).catch((error) => {
        this.errorMessage = {
          message: `There was an error, ${error.message} please try again.`,
          status: 'error',
          duration: 3500
        }
        this.showErrorMessage = true
        this.formSubmitting = false
      })
    },
    startSession () {
      // if there is no appointment or magic token don't start
      if (!this.appointment && !this.magicToken) {
        return
      }

      // create session
      const practiceUuid = this.practiceInfo.practice_id
      const practiceChannelId = `doc-${practiceUuid}`
      const registeredTime = Date.now()
      const isActive = Date.now()

      const startSessionPayload = {
        practiceUuid,
        practiceChannelId,
        registeredTime,
        isActive
      }
      this.sessionPayloadTemp = startSessionPayload

      // if there is a created appointment from the form response
      if (this.appointment) {
        startSessionPayload.appointmentUuid = this.appointment.uuid
        startSessionPayload.appointmentType = APPOINTMENT_TYPE_DROP_IN
      }

      // if there is magic token
      if (this.magicToken) {
        startSessionPayload.jwt = this.magicToken
        startSessionPayload.appointmentType = APPOINTMENT_TYPE_SCHEDULED
        this.$store.dispatch('setAppointmentType', APPOINTMENT_TYPE_SCHEDULED)
      }

      this.$store.dispatch('visitSession/register', startSessionPayload).then((response) => {
        const channelId = response.data.channelId
        localStorage.setItem(LOCAL_STORAGE_CHANNEL_ID_KEY, channelId)
        this.$store.dispatch('visitSession/connectToSession', { store: this.$store, channelId }).finally(() => {
          this.$router.push({
            name: 'ViewsVisit'
          })
        })
      }).catch(() => {
        this.checkingMagicLink = false
        this.invalidMagicLink = true
      })
    },
    onVerificationOfRecaptcha () {
      this.passedRecaptcha = true
      this.showRecaptchaErrorMessage = false
      this.$ppAnalytics.trackEvent({
        action: 'form_input',
        label: 'passedRecaptcha'
      })
    },
    checkRecaptcha () {
      if (!this.passedRecaptcha) {
        this.showRecaptchaErrorMessage = true
      }
    },
    errorMessagesHandler (field, errorField) {
      const errors = []
      if (!this.$v.visit[field].$dirty) {
        return errors
      }
      // Required error message
      this.$v.visit[field].required === false && errors.push(`${errorField} is required`)
      // Default error message
      if (this.$v.visit[field].$invalid && errors.length === 0) {
        errors.push(`${errorField} must be valid`)
      }
      return errors
    }
  },
  validations: {
    visit: {
      firstName: {
        required
      },
      lastName: {
        required
      },
      email: {
        email,
        required
      },
      phone: {
        required,
        validPhone: (v) => validPhone(v)
      }
    }
  }
}
</script>
<style lang="scss" scoped>
.recaptcha-error-message {
  height: 1em;
}
</style>
